import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AssistantTabForm from './AssistantTabForm.tsx';
import { useAssistantConfigForm } from './useAssistantConfigForm.ts';
import Avatar, { AvatarPerson, personLabel, TeamAvatar } from '../../../../core/components/Avatar.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { GET_SMART_INBOX_INTEGRATIONS } from '../../../../customer/queries.ts';
import FieldsConfig from '../../../../document/components/FieldsConfig.tsx';
import { canManageSmartInbox } from '../../../../users/utils.ts';
import { GET_AGENT_MAILBOX_LINKS, GET_PERSONAL_MAILBOX_OWNERS } from '../../queries.ts';

const PREFIX = 'detail.settingsTab.mailboxes';
const COLLAPSED_ROW_COUNT = 5;

interface SmartInboxTrigger {
    id: string;
    name: string;
    agentIds: string[];
}

interface MailboxRow {
    id: string;
    address: string;
    owner?: AvatarPerson;
    isPaused: boolean;
    usedBy?: string;
}

const MailboxRowItem = ({
    mailbox,
    isOn,
    onToggle,
}: {
    mailbox: MailboxRow;
    isOn: boolean;
    onToggle: (isOn: boolean) => void;
}) => {
    const { t } = useTranslation('assistants');
    const details = [
        mailbox.owner && personLabel(mailbox.owner),
        mailbox.isPaused && t(`${PREFIX}.paused`),
        mailbox.usedBy && !isOn && t(`${PREFIX}.usedBy`, { name: mailbox.usedBy }),
    ].filter(Boolean);

    return (
        <label className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-surface-medium">
            {mailbox.owner ? (
                <Avatar person={mailbox.owner} size="md" />
            ) : (
                <TeamAvatar size="md" title={t(`${PREFIX}.sharedMailbox`)} />
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate font-medium text-content-strong">{mailbox.address}</span>
                {details.length > 0 && (
                    <span
                        className={classnames(
                            'truncate text-xs',
                            mailbox.isPaused ? 'text-content-warning-default' : 'text-content-medium'
                        )}
                    >
                        {details.join(' · ')}
                    </span>
                )}
            </span>
            <Switch value={isOn} onValueChange={onToggle} aria-label={mailbox.address} />
        </label>
    );
};

const MailboxGroup = ({
    title,
    mailboxes,
    triggerIds,
    onToggle,
}: {
    title: string;
    mailboxes: MailboxRow[];
    triggerIds: string[];
    onToggle: (id: string, isOn: boolean) => void;
}) => {
    const { t } = useTranslation('assistants');
    const [isExpanded, setExpanded] = useState(false);
    if (!mailboxes.length) return null;

    const hiddenCount = mailboxes.length - COLLAPSED_ROW_COUNT;
    const shown = isExpanded ? mailboxes : mailboxes.slice(0, COLLAPSED_ROW_COUNT);
    const onCount = mailboxes.filter((mailbox) => triggerIds.includes(mailbox.id)).length;

    return (
        <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
                <h3 className="m-0 text-sm font-semibold text-content-strong">{title}</h3>
                <span className="text-xs text-content-medium">
                    {t(`${PREFIX}.count`, { on: onCount, total: mailboxes.length })}
                </span>
            </div>
            <FieldsConfig.Card
                footer={
                    hiddenCount > 0 && (
                        <FieldsConfig.MoreRow
                            isOpen={isExpanded}
                            isAdding
                            label={t(`${PREFIX}.more`, { count: hiddenCount })}
                            onClick={() => setExpanded(!isExpanded)}
                        />
                    )
                }
            >
                {shown.map((mailbox) => (
                    <MailboxRowItem
                        key={mailbox.id}
                        mailbox={mailbox}
                        isOn={triggerIds.includes(mailbox.id)}
                        onToggle={(isOn) => onToggle(mailbox.id, isOn)}
                    />
                ))}
            </FieldsConfig.Card>
        </section>
    );
};

const useMailboxRows = (agentId: string, availableSmartInboxTriggers: SmartInboxTrigger[]) => {
    const { user } = useApplicationContext();
    const { data: personalData } = useQuery(GET_PERSONAL_MAILBOX_OWNERS, { fetchPolicy: 'cache-and-network' });
    // the shared list needs the Smart Inbox permission; without it, shared mailboxes just don't show "Paused"
    const { data: sharedData } = useQuery(GET_SMART_INBOX_INTEGRATIONS, {
        fetchPolicy: 'cache-and-network',
        skip: !canManageSmartInbox(user),
    });
    const { data: agentsData } = useQuery(GET_AGENT_MAILBOX_LINKS, { fetchPolicy: 'cache-and-network' });

    return useMemo(() => {
        const personalById = new Map(
            (personalData?.personalSmartInboxIntegrations || []).map((mailbox: any) => [mailbox.id, mailbox])
        );
        const sharedById = new Map(
            (sharedData?.smartInboxIntegrations || []).map((mailbox: any) => [mailbox.id, mailbox])
        );
        const agentNameById = new Map((agentsData?.agents || []).map((agent: any) => [agent.id, agent.name]));

        return availableSmartInboxTriggers.map((trigger): MailboxRow & { isPersonal: boolean } => {
            const personal: any = personalById.get(trigger.id);
            const shared: any = sharedById.get(trigger.id);
            const otherAgentId = trigger.agentIds?.find((id) => id !== agentId);
            return {
                id: trigger.id,
                address: trigger.name,
                isPersonal: !!personal,
                owner: personal?.createdBy,
                isPaused: (personal ?? shared)?.enabled === false,
                usedBy: otherAgentId ? (agentNameById.get(otherAgentId) as string | undefined) : undefined,
            };
        });
    }, [agentId, availableSmartInboxTriggers, personalData, sharedData, agentsData]);
};

const AssistantMailboxesCard = ({
    agent,
    onChange,
    availableSmartInboxTriggers,
}: {
    agent: any;
    onChange: () => Promise<unknown>;
    availableSmartInboxTriggers: SmartInboxTrigger[];
}) => {
    const { t } = useTranslation('assistants');
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);
    const rows = useMailboxRows(agent.id, availableSmartInboxTriggers);

    const triggerIds: string[] = form.watch('triggerIds') || [];
    const setTriggerIds = (ids: string[]) => form.setValue('triggerIds', ids, { shouldDirty: true });
    const onToggle = (id: string, isOn: boolean) =>
        setTriggerIds(isOn ? [...triggerIds, id] : triggerIds.filter((item) => item !== id));

    // switched-on mailboxes first, sorted from the saved state only, so a row never jumps away while it's toggled
    const savedIds = useMemo(
        () => new Set((agent.smartInboxIntegrations || []).map((integration: any) => integration.id)),
        [agent]
    );
    const sorted = useMemo(
        () =>
            [...rows].sort(
                (a, b) => Number(savedIds.has(b.id)) - Number(savedIds.has(a.id)) || a.address.localeCompare(b.address)
            ),
        [rows, savedIds]
    );
    const shared = sorted.filter((mailbox) => !mailbox.isPersonal);
    const personal = sorted.filter((mailbox) => mailbox.isPersonal);

    return (
        <AssistantTabForm
            title={t(`${PREFIX}.title`)}
            description={t(`${PREFIX}.description`)}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isValid
        >
            {sorted.length ? (
                <>
                    <MailboxGroup
                        title={t(`${PREFIX}.sharedTitle`)}
                        mailboxes={shared}
                        triggerIds={triggerIds}
                        onToggle={onToggle}
                    />
                    <MailboxGroup
                        title={t(`${PREFIX}.personalTitle`)}
                        mailboxes={personal}
                        triggerIds={triggerIds}
                        onToggle={onToggle}
                    />
                </>
            ) : (
                <p className="m-0 text-sm text-content-medium">{t(`${PREFIX}.empty`)}</p>
            )}
        </AssistantTabForm>
    );
};

export default AssistantMailboxesCard;
